import {useEffect, useMemo, useState} from 'react';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {trpc} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {todayDateInputValue} from '@client/pages/account/components/StageResolutionFields';
import {MemberLifecycleDialogFrame} from '@client/pages/client/member-profile/MemberLifecycleDialogFrame';

/** CleverBenefits app relationship values (not VBA codes). */
const RELATIONSHIP_OPTIONS = [
  {value: 'spouse', label: 'Spouse'},
  {value: 'dependent', label: 'Dependent'},
] as const;

interface AddAppDependentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Exactly one of these — a CBS subscriber or an external-TPA member's EDI id. */
  subscriberId?: string;
  externalTpaMemberId?: string;
}

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  birthDate: '',
  relationship: '' as '' | 'spouse' | 'dependent',
  email: '',
  phone: '',
};

export function AddAppDependentDialog({
  open,
  onClose,
  onSuccess,
  subscriberId,
  externalTpaMemberId,
}: AddAppDependentDialogProps) {
  const {toast} = useToast();
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
  }, [open]);

  const setField = (patch: Partial<typeof EMPTY_FORM>) => setForm((prev) => ({...prev, ...patch}));

  const canSubmit = useMemo(
    () =>
      !!form.firstName.trim() &&
      !!form.lastName.trim() &&
      !!form.birthDate &&
      form.birthDate <= todayDateInputValue() &&
      !!form.relationship,
    [form],
  );

  const createDependent = trpc.vba.appUser.createDependent.useMutation({
    onSuccess: () => {
      toast({title: 'Dependent added'});
      onSuccess();
      onClose();
    },
    onError: (err) =>
      toast({title: 'Could not add dependent', description: err.message, variant: 'destructive'}),
  });

  const handleSubmit = () => {
    if (!form.relationship) return;
    const email = form.email.trim();
    const phone = form.phone.trim();
    createDependent.mutate({
      data: {
        relationship: form.relationship,
        userDetails: {
          birthDate: form.birthDate,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          ...(email ? {email} : {}),
          ...(phone ? {phone} : {}),
        },
      },
      ...(externalTpaMemberId ? {externalTpaMemberId} : {subscriberId: subscriberId!}),
    });
  };

  return (
    <MemberLifecycleDialogFrame
      open={open}
      onClose={onClose}
      title='Add dependent'
      description={
        <>
          Adds a family member to this member&apos;s app account. This writes to the CleverBenefits
          app database.
        </>
      }
      contentMaxWidthClassName='sm:max-w-lg'
      cancelDisabled={createDependent.isPending}
      submitDisabled={!canSubmit || createDependent.isPending}
      submitLabel={createDependent.isPending ? 'Adding…' : 'Add dependent'}
      onSubmit={handleSubmit}
    >
      <div className='grid grid-cols-2 gap-3'>
        <div className='space-y-1.5'>
          <Label htmlFor='app-dep-first'>First name</Label>
          <Input
            id='app-dep-first'
            value={form.firstName}
            onChange={(e) => setField({firstName: e.target.value})}
          />
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor='app-dep-last'>Last name</Label>
          <Input
            id='app-dep-last'
            value={form.lastName}
            onChange={(e) => setField({lastName: e.target.value})}
          />
        </div>
      </div>

      <div className='grid grid-cols-2 gap-3'>
        <div className='space-y-1.5'>
          <Label htmlFor='app-dep-dob'>Date of birth</Label>
          <Input
            id='app-dep-dob'
            type='date'
            max={todayDateInputValue()}
            value={form.birthDate}
            onChange={(e) => setField({birthDate: e.target.value})}
          />
        </div>
        <div className='space-y-1.5'>
          <Label>Relationship</Label>
          <Select
            value={form.relationship}
            onValueChange={(v) => setField({relationship: v as typeof form.relationship})}
          >
            <SelectTrigger>
              <SelectValue placeholder='Select' />
            </SelectTrigger>
            <SelectContent>
              {RELATIONSHIP_OPTIONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className='grid grid-cols-2 gap-3'>
        <div className='space-y-1.5'>
          <Label htmlFor='app-dep-email'>Email (optional)</Label>
          <Input
            id='app-dep-email'
            type='email'
            value={form.email}
            onChange={(e) => setField({email: e.target.value})}
          />
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor='app-dep-phone'>Phone (optional)</Label>
          <Input
            id='app-dep-phone'
            type='tel'
            value={form.phone}
            onChange={(e) => setField({phone: e.target.value})}
          />
        </div>
      </div>
    </MemberLifecycleDialogFrame>
  );
}
